import { FormikProvider } from "formik";
import useAddForm from "../hooks/useAddForm";
import AdminInformation from "../add/components/AdminInformation";
import { Button, Toast } from "@/components/kit";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo } from "react";
import KeyHighlights from "./keyHighlights";
import { Spinner } from "@material-tailwind/react";
import { IUser, IUserResponse } from "../types";
import useUpdateUser from "@/hooks/query/admins/useUpdateUser";
import { useQueryClient } from "@tanstack/react-query";

const DetailsTab = ({
  user,
  isFetching,
}: {
  user: IUserResponse | undefined;
  isFetching: boolean;
}) => {
  const formik = useAddForm();
  const { userOrganizations, ...restErrors } = formik.errors;

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const userData: IUser = {
    id: user?.id ?? "",
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    email: user?.email ?? "",
    phoneNumber: user?.phoneNumber ?? "",
    postalCode: user?.postalCode ?? "",
    displayName: user?.displayName ?? "-",
    personalEmail: user?.personalEmail ?? "",
    country: user?.country ?? "",
    province: user?.province ?? "",
    city: user?.city ?? "",
    address: user?.address ?? "",
    birthDate: user?.birthDate ?? "",
    description: user?.description ?? "",
    mfaEnforced: user?.mfaEnforced ?? false,
    mfaAllowedType: user?.preferredMfaType ?? "email",
    // mfaEnrollmentGracePeriod: user?.mfaEnrollmentGracePeriod ?? 3,
    temporaryPassword: user?.temporaryPassword ?? true,
    profile: user?.profile ?? undefined,
    mfaEnrollmentGracePeriod: 3,
    forcedToChangePassword: user?.forcedToChangePassword ?? false,
    roleId: user?.role?.id ?? "",
  };

  const { mutate: updateUser, isPending } = useUpdateUser();

  const cleanedUser = useMemo(() => {
    if (!userData) return null;
    return Object.fromEntries(
      Object.entries(userData).map(([key, value]) => [key, value ?? ""]),
    ) as IUser;
  }, [user]);

  useEffect(() => {
    if (cleanedUser) {
      formik.setValues(cleanedUser);
    }
  }, [cleanedUser]);

  // Check if form values are equal to cleanedUser
  const isFormUnchanged = useMemo(() => {
    if (!cleanedUser) return true;
    // Compare only the fields that exist in cleanedUser
    return Object.keys(cleanedUser).every(
      (key) =>
        formik.values[key as keyof typeof formik.values] ===
        cleanedUser[key as keyof typeof cleanedUser],
    );
  }, [formik.values, cleanedUser]);

  const handleSave = async () => {
    if (!cleanedUser) return;
    const { password, ...rest } = formik.values;
    //i want field with empty value removed from the payload
    const payload = Object.fromEntries(
      Object.entries(rest).filter(([_, value]) => value !== ""),
    );
    updateUser(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["users"],
        });
        navigate("/admins");
        Toast({
          status: "success",
          title: "User updated successfully",
        });
      },
    });
  };

  const handleDiscard = async () => {
    if (!cleanedUser) return;
    formik.resetForm({
      values: cleanedUser,
    });
    // Wait a bit for state to update, then validate
    await new Promise((resolve) => setTimeout(resolve, 0));
    await formik.validateForm();
  };

  if (isFetching)
    return (
      <div className="flex items-center w-full h-full justify-center">
        <Spinner />
      </div>
    );

  return (
    <FormikProvider value={formik}>
      <div className="flex gap-4 pr-4 ">
        <div className="flex-1">
          <div className="px-4 ">
            <AdminInformation userId={user?.id} isEdit />
          </div>
          <div className="mt-12 px-4">
            <div className="flex justify-end gap-4 border border-gray-100 p-4 rounded-xl">
              <Button
                disabled={isFormUnchanged}
                onClick={handleDiscard}
                variant="outlined"
                color="gray"
              >
                Discard
              </Button>
              <Button
                onClick={handleSave}
                disabled={Object.keys(restErrors).length > 0 || isFormUnchanged}
                isLoading={isPending}
                variant="gradient"
                color="gray"
              >
                Save
              </Button>
            </div>
          </div>
        </div>

        <KeyHighlights user={userData} />
      </div>
    </FormikProvider>
  );
};

export default DetailsTab;
